import { useLocation, useSearchParams } from 'react-router-dom'
import {
  Column,
  ColumnDef,
  ColumnSizingInfoState,
  ColumnSizingState,
  RowSelectionState,
  SortingState,
  Table,
  TableMeta,
  Updater,
  VisibilityState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { entries, isEmpty, isEqual } from 'lodash'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { SELECTION_COLUMN_ID, selectionColumn } from './bulkActions/selectionColumn'
import type { BulkActionsConfig } from './bulkActions/types'
import { FilterableColumn, FilterObject } from 'app/filter.utils'
import { useInfiniteScroll } from 'app/hooks/useInfiniteScroll'
import { useSettings } from 'app/hooks/useSettings'
import {
  INITIAL_COLUMN_SIZING_INFO,
  adjustLastColumnWidth,
  calculateColumnWidths,
  getColumnWidthsToSave,
  getScrollbarWidth,
} from 'app/lib/calculateColumnWidths'
import { useHandleSearchParams } from 'app/utils'
import { useAuthStore } from 'app/stores/StoreContext'
import { bg } from 'lib/background'
import { debug } from 'lib/logging'

function isColumnSizingState(value: unknown): value is ColumnSizingState {
  return !!value && typeof value === `object` && !Array.isArray(value)
}

export type UseTableLayoutProps<T> = {
  data: { hits: T[]; total: number } | undefined
  columns: ColumnDef<T, any>[]
  filterableColumns?: FilterableColumn[]
  defaultJoin?: `and` | `or`
  meta?: TableMeta<T>
  /** Appends the next page onto `data`; without it the table stays on its first page. */
  loadMore?: () => Promise<unknown>
  extraButtons?: (columns: ColumnDef<T, any>[], columnVisibility: VisibilityState) => ReactElement[]
  hideControls?: boolean
  drawer?: ReactElement | null
  initialSort?: SortingState
  initialFilters?: FilterObject[]
  bulkActions?: BulkActionsConfig<T>
  /**
   * Starting widths as proportions by column id, redone whenever a column is shown or hidden,
   * until the user resizes one; only a user's resize is saved, never the starting split.
   */
  defaultColumnWeights?: Record<string, number>
}

export type UseTableLayoutReturn<T> = {
  table: Table<T>
  bulkActions?: BulkActionsConfig<T>
  selectionMode: boolean
  toggleSelectionMode: () => void
  exitSelectionMode: () => void
  items: T[]
  columns: ColumnDef<T, any>[]
  filterableColumns?: FilterableColumn[]
  defaultJoin: `and` | `or`
  hideControls: boolean
  extraButtons?: (columns: ColumnDef<T, any>[], columnVisibility: VisibilityState) => ReactElement[]
  drawer: ReactElement | null
  initialFilters?: FilterObject[]
  columnVisibility: VisibilityState
  defaultColumnVisibility: Record<string, boolean>
  initialColumnOrder: string[]
  sortOrder: { columnId?: string; order?: `asc` | `desc` }
  visibilityReady: boolean
  loading: boolean
  tableWrapperRef: React.RefObject<HTMLDivElement | null>
  toggleSort: (column: Column<T, unknown>) => void
  onScroll: (e: any) => void
  onResetSizing: () => void
  onAutoFitColumn: (columnId: string, width: number) => void
  cookieName: string
  pathname: string
  tab: string | null
}

export function useTableLayout<T>({
  data,
  columns: inputColumns,
  filterableColumns,
  defaultJoin = `and`,
  meta,
  loadMore,
  hideControls = false,
  extraButtons,
  drawer = null,
  initialSort,
  initialFilters,
  bulkActions,
  defaultColumnWeights,
}: UseTableLayoutProps<T>): UseTableLayoutReturn<T> {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const [selectionMode, setSelectionMode] = useState(false)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  // Anchor row (id) + last shift-key state, for shift-click range selection.
  const selectionAnchorRef = useRef<string | null>(null)
  const shiftKeyRef = useRef(false)

  // Prepend the bulk-select checkbox column when bulk actions are enabled. It
  // stays in the column set permanently (so column order is stable) and is only
  // made visible while in selection mode, via the forced visibility below.
  const columns = useMemo(
    () => (bulkActions ? [selectionColumn<T>(selectionAnchorRef, shiftKeyRef), ...inputColumns] : inputColumns),
    [inputColumns, bulkActions],
  )

  const defaultColumnVisibility = Object.fromEntries(
    columns?.map((column) => [column.id as string, column.meta?.[`defaultVisible`] ?? false]),
  )
  const [columnVisibility, setColumnVisibility] = useState<{
    [k: string]: boolean
  }>(Object.fromEntries(columns?.map((column) => [column.id as string, false])))
  const [visibilityReady, setVisibilityReady] = useState(false)
  const cookieName = `columnSizing-${
    searchParams.get(`tab`) ? location.pathname + searchParams.get(`tab`) : location.pathname
  }`
  const visibilitySettingName = `columnVisibility-${
    searchParams.get(`tab`) ? location.pathname + searchParams.get(`tab`) : location.pathname
  }`
  const [settings, setSettings, settingsLoaded, settingsReadFailed] = useSettings(cookieName)
  const settingsRef = useRef(settings)
  const initialColumnOrder = columns?.map((column) => column.id as string)
  const userHasInteractedWithSort = useRef(false)
  const [sortOrder, setSortOrder] = useState<{ columnId?: string; order?: `asc` | `desc` }>(() => {
    const urlSort = searchParams.get(`sort`)
    if (urlSort && urlSort !== ``) {
      return {
        columnId: columns.find((column) => column.meta?.[`sortKey`] === urlSort.split(`:`)[0])?.id,
        order: urlSort.split(`:`)[1] as `asc` | `desc`,
      }
    }
    if (urlSort === ``) {
      userHasInteractedWithSort.current = true
    }
    return {}
  })
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})
  const columnSizingRef = useRef<ColumnSizingState>(columnSizing)
  const [columnSizingInfo, setColumnSizingInfo] = useState<ColumnSizingInfoState>(INITIAL_COLUMN_SIZING_INFO)
  const [sizingInitialized, setSizingInitialized] = useState(false)

  const tableWrapperRef = useRef<HTMLDivElement>(null)
  const resizingColumnRef = useRef<string | false>(false)
  const lastColManualWidthRef = useRef<number | null>(null)
  const visibilityInitialized = useRef(false)
  const auth = useAuthStore()
  const appData = auth as { userSettings?: { key: string; value: unknown }[] }

  useEffect(() => {
    columnSizingRef.current = columnSizing
  }, [columnSizing])

  // Sync sortOrder from initialSort when it first becomes defined (useInitialTableSort
  // populates asynchronously via an effect). Skip if user has already interacted with sort
  // or if there's a URL sort param.
  useEffect(() => {
    if (userHasInteractedWithSort.current) return
    if (searchParams.get(`sort`)) return
    if (!initialSort || initialSort.length === 0) return
    setSortOrder({
      columnId: initialSort[0].id,
      order: initialSort[0].desc ? `desc` : `asc`,
    })
  }, [initialSort])

  useEffect(() => {
    settingsRef.current = settings
  }, [settings])

  useEffect(() => {
    if (visibilityInitialized.current) return

    const userSettings = appData?.userSettings
    if (userSettings) {
      const savedColumns = userSettings.find((s) => s.key === visibilitySettingName)?.value as
        | Record<string, { visible: boolean; order: number }>
        | undefined
      if (savedColumns) {
        const vis = entries(defaultColumnVisibility).reduce(
          (acc, [key, val]) => {
            acc[key] = savedColumns[key]?.visible ?? val ?? false
            return acc
          },
          {} as Record<string, boolean>,
        )
        setColumnVisibility(vis)
      } else {
        setColumnVisibility(defaultColumnVisibility)
      }
    } else {
      setColumnVisibility(defaultColumnVisibility)
    }
    visibilityInitialized.current = true
    setVisibilityReady(true)
  }, [appData, visibilitySettingName, defaultColumnVisibility])

  useEffect(() => {
    if (!visibilityReady || sizingInitialized) return
    if (!settingsLoaded) return
    const containerWidth = tableWrapperRef.current?.clientWidth
    if (!containerWidth) {
      const timer = setTimeout(() => setSizingInitialized(false), 100)
      return () => clearTimeout(timer)
    }

    const savedSizing = isColumnSizingState(settings) ? settings : {}
    const { sizing, sizingToSave, lastColumnSavedWidth } = calculateColumnWidths(
      table,
      containerWidth,
      savedSizing,
      60,
      defaultColumnWeights,
    )
    debug(`columns`, `initializing`, { containerWidth, settings, sizing, sizingToSave })

    if (lastColumnSavedWidth !== null) {
      lastColManualWidthRef.current = lastColumnSavedWidth
    }
    if (!isEmpty(sizing)) {
      setColumnSizing(sizing)
      // A failed read looks like nothing saved; saving defaults then would overwrite the user's widths.
      if (isEmpty(savedSizing) && !isEmpty(sizingToSave) && !settingsReadFailed && !defaultColumnWeights) {
        bg(async () => setSettings(cookieName, sizingToSave))
      }
    }
    setSizingInitialized(true)
  }, [visibilityReady, sizingInitialized, settings, settingsLoaded])

  useEffect(() => {
    if (!sizingInitialized) return
    if (resizingColumnRef.current !== false) return
    const containerWidth = tableWrapperRef.current?.clientWidth
    if (!containerWidth) return

    // Merge current sizing with saved settings — saved settings take priority so that
    // columns initialized with defaults get corrected to their saved widths
    const currentSettings = isColumnSizingState(settingsRef.current) ? settingsRef.current : {}
    const mergedSizing =
      defaultColumnWeights && isEmpty(currentSettings) ? {} : { ...columnSizingRef.current, ...currentSettings }
    const { sizing } = calculateColumnWidths(table, containerWidth, mergedSizing, 60, defaultColumnWeights)
    if (!isEmpty(sizing) && !isEqual(sizing, columnSizingRef.current)) {
      setColumnSizing(sizing)
    }
  }, [columnVisibility])

  useEffect(() => {
    if (!sizingInitialized || !tableWrapperRef.current) return

    const observer = new ResizeObserver((entries) => {
      const containerWidth = entries[0]?.contentRect.width
      if (!containerWidth) return
      if (resizingColumnRef.current !== false) return

      const currentSettings = isColumnSizingState(settingsRef.current) ? settingsRef.current : {}
      const { sizing } = calculateColumnWidths(table, containerWidth, currentSettings, 60, defaultColumnWeights)
      if (!isEmpty(sizing) && !isEqual(sizing, columnSizingRef.current)) {
        setColumnSizing(sizing)
      }
    })

    observer.observe(tableWrapperRef.current)
    return () => observer.disconnect()
  }, [sizingInitialized])

  const [items, , onScroll] = useInfiniteScroll(data?.hits, data?.total, loadMore)
  // `data` is `undefined` until the first fetch resolves. Surface this as an explicit loading
  // flag so the table can show a spinner on first load instead of rendering an empty header-only
  // table (which looked like "data not found" before rows arrived) (TRK-5196).
  const loading = data === undefined

  // TanStack Table exposes unstable callbacks by design; the React compiler cannot memoize this API safely.
  // oxlint-disable-next-line react/incompatible-library
  const table = useReactTable({
    data: items as T[],
    columns,
    columnResizeMode: `onChange`,
    onColumnSizingInfoChange: (updater) => {
      setColumnSizingInfo((prev) => {
        const newInfo = typeof updater === `function` ? updater(prev) : updater
        resizingColumnRef.current = newInfo.isResizingColumn
        return newInfo
      })
    },
    state: {
      columnVisibility: bulkActions ? { ...columnVisibility, [SELECTION_COLUMN_ID]: selectionMode } : columnVisibility,
      columnSizing,
      columnSizingInfo,
      ...(bulkActions && { rowSelection }),
    },
    ...(meta && { meta }),
    ...(bulkActions && {
      enableRowSelection: true,
      onRowSelectionChange: setRowSelection,
      // Key selection by the row's stable id — never the array index, which would
      // re-map an existing selection onto different rows after a refetch/reorder.
      // Consumers of bulkActions supply ids; the apply loop reports missing-id rows.
      getRowId: (row: any) => String(row?.id),
    }),
    initialState: { columnVisibility, columnOrder: initialColumnOrder },
    defaultColumn: {
      size: 250,
      minSize: 60,
    },
    onColumnVisibilityChange: (_columnVisibility: Updater<VisibilityState>) => {
      if (typeof _columnVisibility === `function`) {
        _columnVisibility = _columnVisibility(columnVisibility)
      }
      setColumnVisibility(_columnVisibility)
      const newColumnEntries = entries(_columnVisibility).filter(([, visible]) => visible === true)
      const oldColumnEntries = entries(columnVisibility).filter(([, visible]) => visible === true)
      if (newColumnEntries.length > oldColumnEntries.length) {
        let newColumns = newColumnEntries.filter((col) => !oldColumnEntries.find((oldEntry) => oldEntry[0] === col[0]))
        let newColumnWidth = 250
        let newColumnWidths = newColumns.reduce((acc, col) => ({ ...acc, [col[0]]: newColumnWidth }), {})
        const availableWidth = (tableWrapperRef.current?.clientWidth ?? 0) - getScrollbarWidth()
        if (
          availableWidth > 0 &&
          entries(table.getState().columnSizing).reduce((acc, [k, v]) => acc + v, 0) +
            newColumns.length * newColumnWidth >
            availableWidth &&
          entries(table.getState().columnSizing).reduce((acc, [k, v]) => acc + v, 0) <= availableWidth + 100
        ) {
          let columns = table.getState().columnOrder
          let columnSizing = table.getState().columnSizing
          let visibleColumns = columns.filter((id) => table.getColumn(id)?.getIsVisible())
          let lastColumn = visibleColumns[visibleColumns.length - 1]
          columnSizing[lastColumn] = newColumnWidth
          setColumnSizing({ ...columnSizing, ...newColumnWidths })
        }
      }
    },
    getCoreRowModel: getCoreRowModel(),
    onColumnSizingChange: (_columnSizing: Updater<ColumnSizingState>) => {
      if (typeof _columnSizing === `function`) {
        _columnSizing = _columnSizing(columnSizing)
      }
      const containerWidth = tableWrapperRef.current?.clientWidth
      if (containerWidth) {
        lastColManualWidthRef.current = adjustLastColumnWidth(
          table,
          _columnSizing,
          columnSizing,
          containerWidth,
          // +6 for the DataTable scroll container's `pr-1.5` right padding, so
          // the last column fills to the visible area and doesn't force a
          // horizontal scrollbar.
          getScrollbarWidth() + 6,
          resizingColumnRef.current,
          lastColManualWidthRef.current,
        )
      }
      setColumnSizing(_columnSizing)
    },
    sortingFns: {
      onlySortParentRows: (rowA, rowB, columnId) => {
        if (rowA.depth === 0 && rowB.depth === 0) {
          return (rowA.getValue(columnId) as string) > (rowB.getValue(columnId) as string)
            ? 1
            : (rowA.getValue(columnId) as string) < (rowB.getValue(columnId) as string)
              ? -1
              : 0
        }
        return 0
      },
    },
  })

  // Track when user was actively resizing (drag handle released)
  const wasResizingRef = useRef(false)

  // Double-click-to-fit lands here. Going through `table.setColumnSizing` keeps the
  // last-column adjustment that a drag gets, and flagging it as a resize is what
  // lets the save effect below persist the new width.
  const onAutoFitColumn = (columnId: string, width: number) => {
    wasResizingRef.current = true
    table.setColumnSizing((prev) => ({ ...prev, [columnId]: width }))
  }

  useEffect(() => {
    if (columnSizingInfo.isResizingColumn) {
      wasResizingRef.current = true
    }
  }, [columnSizingInfo.isResizingColumn])

  // Save column widths only when user finishes a resize drag (not on init/visibility changes)
  useEffect(() => {
    if (!wasResizingRef.current) return
    if (columnSizingInfo.isResizingColumn !== false) return
    if (isEmpty(columnSizing)) return

    wasResizingRef.current = false
    const sizingToSave = getColumnWidthsToSave(table, columnSizing, true)
    if (!isEmpty(sizingToSave) && !isEqual(settings, sizingToSave)) {
      bg(async () => setSettings(cookieName, sizingToSave))
    }
  }, [columnSizingInfo, columnSizing])

  const toggleSort = (column: Column<T, unknown>) => {
    userHasInteractedWithSort.current = true
    if (column.id === sortOrder.columnId) {
      switch (sortOrder.order) {
        case undefined:
          setSortOrder({ columnId: column.id, order: `asc` })
          break
        case `asc`:
          setSortOrder({ columnId: column.id, order: `desc` })
          break
        case `desc`:
          setSortOrder(column.columnDef.meta?.alwaysSorted ? { columnId: column.id, order: `asc` } : {})
          break
      }
    } else {
      setSortOrder({ columnId: column.id, order: `asc` })
    }
  }

  useEffect(() => {
    if (!userHasInteractedWithSort.current) return
    const selectedColumn = columns.find((column) => column.id === sortOrder.columnId)
    const sortKey =
      selectedColumn && selectedColumn.meta && selectedColumn.meta[`sortKey`]
        ? `${selectedColumn.meta[`sortKey`]}`
        : undefined
    if (sortKey) {
      handleSearchParams({
        sort: `${sortKey}:${sortOrder.order}${
          selectedColumn!.meta![`missing`] ? `:${selectedColumn!.meta![`missing`]}` : ``
        }`,
      })
    } else {
      handleSearchParams({ sort: `` })
    }
  }, [sortOrder])

  const onResetSizing = () => {
    setColumnSizing({})
    bg(async () => setSettings(cookieName, {}))
  }

  const exitSelectionMode = () => {
    setSelectionMode(false)
    setRowSelection({})
  }

  const toggleSelectionMode = () => {
    setSelectionMode((prev) => {
      if (prev) setRowSelection({})
      return !prev
    })
  }

  return {
    table,
    bulkActions,
    selectionMode,
    toggleSelectionMode,
    exitSelectionMode,
    items,
    columns,
    filterableColumns,
    defaultJoin,
    hideControls,
    extraButtons,
    drawer,
    initialFilters,
    columnVisibility,
    defaultColumnVisibility,
    initialColumnOrder,
    sortOrder,
    visibilityReady,
    loading,
    tableWrapperRef,
    toggleSort,
    onScroll,
    onResetSizing,
    onAutoFitColumn,
    cookieName,
    pathname: location.pathname,
    tab: searchParams.get(`tab`),
  }
}
